/**
 * Accounts Domain — Public API (Barrel Export)
 *
 * This is the public interface for the accounts domain.
 * All account-related imports should come through this barrel.
 *
 * Split from the 878-line accountHelpers.ts god file into 5 focused modules (R18):
 * - creation.ts: Account creation & ID generation
 * - queries.ts: Lookups, getters, validation
 * - updates.ts: Field updates & deletion
 * - relationships.ts: Parent/teacher linking
 * - classrooms.ts: Classroom CRUD
 *
 * Additional modules consolidated (R19):
 * - migration.ts: V3 state load/save/create (from utils/accountMigration.ts)
 * - auth.ts: Password, session, login helpers (from utils/authHelpers.ts)
 * - uiHelpers.ts: Role icons, labels, colors (from utils/accountUIHelpers.ts)
 *
 * Types added (R30): Account types moved from types/accounts.ts
 */

// ── Creation ─────────────────────────────────────────────────
export {
  generateAccountId,
  createDefaultAdminAccount,
  ensureAdminAccount,
  createAccountInState,
} from './logic/creation'

// ── Queries ──────────────────────────────────────────────────
export {
  getActiveAccount,
  getAccountById,
  switchActiveAccount,
  getLinkedLearners,
  getLinkedParents,
  getContextLearnerAccount,
  validateAccountsState,
  getUniqueSchoolNames,
} from './logic/queries'

// ── Updates ──────────────────────────────────────────────────
export {
  updateAccountName,
  updateAccountEmail,
  updateAccountPassword,
  updateTeacherDetails,
  updateLearnerLinkCode,
  updateLearnerGrade,
  updateLearnerDetails,
  touchLastActive,
  deleteAccountFromState,
  completeOnboarding,
  prepareAccountDeletion,
  executeAccountDeletion,
  validateEmailUpdate,
  ORPHAN_TEACHER_PREFIX,
  isOrphanedTeacherId,
} from './logic/updates'
export type {
  AccountDeletionDecision,
  AccountDeletionPreparation,
} from './logic/updates'

// ── Relationships ────────────────────────────────────────────
export {
  MAX_PARENTS_PER_LEARNER,
  linkParentToLearner,
  linkTeacherToLearner,
  unlinkParentFromLearner,
  unlinkTeacherFromLearner,
  restoreArchivedLearner,
} from './logic/relationships'

// ── Classrooms ───────────────────────────────────────────────
export {
  generateClassroomId,
  generateJoinCode,
  generateUniqueJoinCode,
  getTeacherClassrooms,
  getOwnedClassrooms,
  getClassroomRole,
  MAX_CO_TEACHERS_PER_CLASSROOM,
  findClassroomByJoinCode,
  addClassroom,
  updateClassroom,
  removeClassroom,
  addLearnerToClassroom,
  removeLearnerFromClassroom,
  getClassroomStudents,
  joinClassroomByCode,
  regenerateJoinCode,
  findDuplicateClassroomName,
  getLearnerExistingClassrooms,
  validateAddLearnerToClassroom,
  validateNewClassroom,
  getSchoolClassrooms,
  getAllClassroomsWithTeachers,
  transferClassroomOwnership,
  joinClassroomAsCoTeacher,
  leaveClassroomAsCoTeacher,
} from './logic/classrooms'
export type { ClassroomWithTeacher } from './logic/classrooms'
export type { ClassroomValidationWarning } from './logic/classrooms'

// ── Orphans ──────────────────────────────────────────────────
export { detectOrphanedClassrooms, cleanupOrphans } from './logic/orphans'
export type { OrphanReport } from './logic/orphans'

// ── Unified Linkage (canonical — replaces validateAddLearnerToClassroom +
//    getLearnerClassroomFlags + ad-hoc parent validation) ─────────────
export {
  validateLink,
  getLearnerLinkageFlags,
  hasAnyLinkageFlag,
  hasGatingWarning,
  hasBlockingWarning,
} from './logic/learnerLinkage'
export type {
  LinkContext,
  LinkWarning,
  LinkWarningSeverity,
  LearnerLinkageFlags,
  LinkageFlagsOptions,
} from './logic/learnerLinkage'

// ── Permissions ──────────────────────────────────────────────
export {
  canPerform,
  getAllowedActions,
  roleHasPermission,
} from './logic/permissions'
export type { PermissionAction, PermissionContext } from './logic/permissions'

// ── Migration (V4 State — current) ──────────────────────────
export {
  CURRENT_SCHEMA_VERSION,
  loadState,
  createFreshState,
  saveState,
} from './logic/migration'
export type { MultiAccountStateV4 } from './logic/migration'

// ── Auth Helpers ─────────────────────────────────────────────
export {
  hashPassword,
  verifyPassword,
  generateLinkCode,
  findAccountByLinkCode,
  findAccountByUsername,
  findAccountByEmail,
  isAllDemoAccounts,
  hasProductionAccounts,
  suggestUsername,
  isValidUsername,
  isValidPassword,
  isSessionExpired,
  isUsernameAvailable,
  isEmailAvailable,
  generateUniqueLinkCode,
  sanitizeDisplayName,
  sanitizeEmail,
  validateRegistrationInput,
} from './logic/auth'

// ── Auth Service (Backend-Ready Abstraction) ─────────────────
export type {
  AuthTokens,
  AuthSession,
  LoginResult,
  RegisterResult,
} from './logic/authService'
export {
  loginWithEmail,
  loginWithUsername,
  // loginWithLinkCode is verwijderd als inlogmethode (14 juli 2026).
  // Koppelcodes bewijzen een relatie, geen identiteit — zie
  // docs/project/SUPABASE_AUTH_MIGRATION.md §3.
  getRateLimitRemaining,
  secureRandom,
  secureRandomString,
  getAccessToken,
  isAuthenticated,
  refreshSession,
  logout,
} from './logic/authService'

// ── UI Helpers ───────────────────────────────────────────────
export {
  getAccountIcon,
  getAccountRoleLabel,
  ACCOUNT_ROLE_COLORS,
} from './logic/uiHelpers'

// ── Mastery Queries (pure computation helpers) ───────────────
//
// Demo seeding has moved to the dedicated `src/demo/` DDD domain
// (Boot Flow Refactor — mei 2026). The accounts barrel no longer
// re-exports demo functions to keep this module focused on auth/CRUD.
export { createMasteryQueries } from './hooks/useLearnerActions'

// ── UI Components ────────────────────────────────────────────
export { LoginScreen } from './components/LoginScreen'
export { RegisterScreen } from './components/RegisterScreen'
export { WelcomeScreen } from './components/WelcomeScreen'
export { AccountSwitcher } from './components/AccountSwitcher'
export { AccountSwitcherDropdown } from './components/AccountSwitcherDropdown'
export { ProfileButton } from './components/ProfileButton'
export { ProfileSection } from './components/ProfileSection'

// ── Onboarding Components ───────────────────────────────────
// Note: JoinClassroomPrompt / LinkChildPrompt / CreateFirstClassroom zijn
// verwijderd in Sprint 2.8 (zie AUTH_ONBOARDING_DOMAIN_RC_AUDIT_2026.md §23).
// Alle child/classroom-linking gebeurt nu in de portal-empty-states.
export { OnboardingWizard } from './components/OnboardingWizard'
export {
  OnboardingProgress,
  OnboardingWelcomeStep,
} from './components/onboarding'

// ── Types (canonical source — moved from types/accounts.ts in R30) ───────────
export type {
  AccountRole,
  LearnerAccount,
  ParentAccount,
  Classroom,
  TeacherAccount,
  AdminAccount,
  Account,
  RelationshipGraph,
  AccountsState,
  ActiveContext,
  OnboardingStepState,
} from './types'
export {
  isLearnerAccount,
  isParentAccount,
  isTeacherAccount,
  isAdminAccount,
} from './types'
